/*
 *  Copyright (c) 2020-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import dayjs from "dayjs";
import { FlexContainer, Text, theme } from "@nordcloud/gnui";
import { DateRange, EstatePeriod, Separator } from "~/components";
import { dateFormat } from "~/constants";
import { isNotNil } from "~/tools";
import { CostData } from "./CostData";

type Props = {
  period: EstatePeriod;
  range: DateRange | undefined;
  costLoading: boolean;
  totalCost?: string;
  count?: number | null;
  monthlySavings?: string | null;
};

export function EstateTotals({
  period,
  range,
  costLoading = false,
  totalCost,
  count,
  monthlySavings,
}: Props) {
  return (
    <>
      <Text
        weight="medium"
        ml={theme.spacing.spacing03}
        mb={theme.spacing.spacing02}
      >
        {getSelectedDates(period, range)}
      </Text>
      <Separator />
      <FlexContainer mt={theme.spacing.spacing02}>
        <CostData
          totalCost={totalCost}
          count={count}
          monthlySavings={monthlySavings}
          isLoading={costLoading}
        />
      </FlexContainer>
    </>
  );
}

function getSelectedDates(period: EstatePeriod, range?: DateRange) {
  if (period === EstatePeriod.RANGE && isNotNil(range)) {
    return getRangeDates(range);
  }

  return getMonthDates(period);
}

function getMonthDates(period: EstatePeriod) {
  const today = dayjs();
  if (period === EstatePeriod.PREVIOUS_MONTH) {
    const previousMonth = today.add(-1, "month").endOf("month").toString();

    return `1 - ${formatDate(previousMonth)}`;
  }

  return `1 - ${formatDate(today.toString())}`;
}

function getRangeDates(range: DateRange) {
  const { from, to } = range;
  return `${formatDate(from)} - ${formatDate(to)}`;
}

function formatDate(date: string) {
  return `${dayjs(date).format(dateFormat.dayShort)} ${dayjs(date).format(
    dateFormat.monthName
  )} ${dayjs(date).format(dateFormat.year)}`;
}
